<template>
    <div class="flex flex-col">
        <div class="flex justify-center items-center my-6" v-if="showTime">
            <p class="mx-4 text-sm font-regular uppercase text-gray-600" v-text="formattedTime" />
        </div>

        <div class="flex justify-center items-center my-3" v-if="message.type == 'numberChange'">
            <conversation-card class="flex items-center">
                <template #description>
                    <div
                        class="text-sm text-gray-600"
                        v-html="$t('chat.messageList.numberChange', {
                            newNumber: message.new_number,
                            oldNumber: message.old_number,
                        })"
                    />
                </template>
            </conversation-card>
        </div>
        <template v-else>
            <div class="flex" :class="direction == 'inbound' ? 'justify-start' : 'justify-end'">
                <div
                    class="w-auto max-w-3/4 h-auto rounded-t-xl shadow-lg bg-white overflow-hidden | md:max-w-2/3 | xl:max-w-2/5"
                    :class="messageClasses"
                    ref="wrapper"
                    v-tooltip="{
                        content: formattedDate,
                        placement: tooltipPlacement,
                        showOnMobile: true,
                    }"
                >
                    <img
                        :alt="attachment.name"
                        :src="attachment.url"
                        :style="attachment.style"
                        :key="attachment.name"
                        v-img
                        v-for="attachment in attachments"
                    >

                    <p
                        class="w-full p-3 break-anywhere"
                        :class="{ 'pt-2': attachments.length }"
                        v-html="formattedMessage"
                        v-if="formattedMessage"
                    />
                </div>
            </div>
            <div class="inline-flex items-center justify-end mt-2" v-if="failed">
                <div
                    class="link-red | inline-flex items-center font-medium"
                    v-tooltip="{ content: $t('chat.message.errorNoAccess'), placement: ['xs', 'sm'].includes($mq) ? 'bottom-end' : 'left' }"
                    v-if="accessError"
                >
                    <nio-icon class="mr-1" name="regular/alert-circle" />
                    {{ $t('chat.message.notDeliveredNoAccess') }}
                </div>
                <div
                    class="link-red | inline-flex items-center font-medium"
                    v-else-if="unreachableNoRetry"
                >
                    {{ $t('chat.message.notDeliveredUnreachable') }}
                </div>
                <div
                    class="link-red | inline-flex items-center font-medium"
                    @click="retry"
                    v-else-if="unreachableWithRetry"
                >
                    {{ $t('chat.message.notDeliveredUnreachableWithRetry') }}
                </div>
                <button
                    class="link-red | inline-flex items-center font-medium"
                    @click="retry"
                    v-tooltip="{ content: $t('chat.message.error'), placement: ['xs', 'sm'].includes($mq) ? 'bottom-end' : 'left' }"
                    v-else
                >
                    <nio-icon class="mr-1" name="regular/alert-circle" />
                    {{ $t('chat.message.notDelivered') }}
                </button>
            </div>

            <conversation-card class="my-4" :icon="appointmentIcon" v-if="isConfirmationReply">
                <template #title>
                    <div
                        class="text-xl"
                        :class="appointmentColor"
                        v-tooltip="appointmentResponseDateTime"
                        v-text="appointmentStatus"
                    />
                </template>
                <template #description>
                    <div>
                        <div class="mb-1">
                            {{ $t('chat.messageList.appointmentTitle') }}
                        </div>
                        <div>
                            <span class="font-semibold">{{ appointmentDate }}</span> - {{ appointmentTime }}
                        </div>
                    </div>
                </template>
            </conversation-card>

            <conversation-card class="my-4" icon="light/accounting-invoice-mail" v-if="isPaymentReply">
                <template #title>
                    <div class="text-xl text-green-500" v-text="$t('chat.messageList.paid')" />
                </template>
                <template #description>
                    <div>
                        <div class="mb-1">
                            {{ $t('chat.messageList.paymentTitle') }}
                        </div>
                        <div class="font-semibold">
                            {{ $n(paymentAmountInDollars, 'currency') }}
                        </div>
                    </div>
                </template>
            </conversation-card>
        </template>
    </div>
</template>

<script>
    import linkifyElement from 'linkifyjs/element';
    import { mapMutations, mapState } from 'vuex';
    import { shortnameToOutput } from '@/utils/emoji.js';

    import ConversationCard from '@/components/Chat/ConversationCard.vue';

    export default {
        components: {
            ConversationCard,
        },

        props: {
            message: {
                type: Object,
                required: true,
            },
            inactive: {
                type: Boolean,
                required: true,
            },
            showTime: {
                type: Boolean,
                required: true,
            },
        },

        data() {
            return {
                wrapperWidth: 0,
            };
        },

        computed: {
            attachments() {
                return (this.message.attachments || []).map(attachment => {
                    return {
                        ...attachment,
                        style: this.getAttachmentStyle(attachment),
                    };
                });
            },

            unreachableNoRetry() {
                return this.message.status == 'undelivered' && this.message.error_code == '30006';
            },

            unreachableWithRetry() {
                return this.message.status == 'undelivered' && this.message.error_code == '30005';
            },

            direction() {
                return this.message.direction == 'I' ? 'inbound' : 'outbound';
            },

            failed() {
                return this.message.status == 'failed' || this.message.status == 'undelivered';
            },

            formattedDate() {
                const format = this.$i18n.locale == 'en' ? 'D MMM YYYY h:mm:ss A' : 'D MMM YYYY H:mm:ss';

                return moment(this.message.created_at).format(format);
            },

            formattedTime() {
                return this.formatTime(this.message.created_at);
            },

            formattedMessage() {
                if (empty(this.message.body)) {
                    return '';
                }

                // Convert line breaks
                const body = this.message.body.replace(/(?:\r\n|\r|\n)/g, '<br>');

                // Convert emojis
                return shortnameToOutput(body);
            },

            messageClasses() {
                let messageClasses = 'rounded-br-xl rounded-bl-sm';

                if (this.direction == 'outbound') {
                    messageClasses = 'rounded-bl-xl rounded-br-sm bg-gradient-blue text-white';
                }

                return messageClasses;
            },

            appointmentIcon() {
                switch (this.message.appointment.status) {
                    case 'confirmed':
                        return 'light/calendar-check-1';
                    case 'canceled':
                        return 'light/calendar-disable-1';
                    default:
                        return 'light/calendar-warning';
                }
            },

            appointmentColor() {
                switch (this.message.appointment.status) {
                    case 'confirmed':
                        return 'text-green-500';
                    case 'canceled':
                        return 'text-red-500';
                    default:
                        return 'text-yellow-500';
                }
            },

            appointmentStatus() {
                let responseMethodText = ' ';

                if (this.message.appointment.status_updated_by == 'sms') {
                    responseMethodText += this.$t('chat.messageList.bySms');
                }

                if (this.message.appointment.status_updated_by == 'email') {
                    responseMethodText += this.$t('chat.messageList.byEmail');
                }
                switch (this.message.appointment.status) {
                    case 'confirmed':
                        return this.$t('chat.messageList.statusConfirmed') + responseMethodText;
                    case 'canceled':
                        return this.$t('chat.messageList.statusCanceled') + responseMethodText;
                    case 'unknown':
                        return this.$t('chat.messageList.statusUnknown');
                    default:
                        return '';
                }
            },

            appointmentResponseDateTime() {
                return this.formatDateTime(this.message.appointment.status_updated_at);
            },

            appointmentDate() {
                return this.formatDate(this.message.appointment.datetime);
            },

            appointmentTime() {
                return this.formatTime(this.message.appointment.datetime);
            },

            paymentAmountInDollars() {
                return this.message.payment.amount / 100;
            },

            isConfirmationReply() {
                return this.message.direction == 'O' &&
                    this.message.appointment &&
                    this.message.appointment.status;
            },

            isPaymentReply() {
                return this.message.payment &&
                    this.message.payment.paid_at != null &&
                    this.message.direction == 'O' &&
                    this.message.automated;
            },

            accessError() {
                return this.message.error_code == 'atx01';
            },

            tooltipPlacement() {
                if (['xs', 'sm', 'md', 'lg'].includes(this.$mq)) {
                    return this.direction == 'inbound'
                        ? 'bottom-end'
                        : 'bottom-start';
                }

                return this.direction == 'inbound'
                    ? 'right'
                    : 'left';
            },
        },

        methods: {
            ...mapMutations('chat', [
                'addMessage',
                'setSending',
            ]),

            ...mapState('setting', [
                'users',
            ]),

            getUser(id) {
                return this.users.find(user => user.id == id);
            },

            async retry() {
                this.setSending(true);

                try {
                    const response = await this.$axios.post(`messages/retry/${this.message.id}`, this.message);
                    const data = response.data.data;

                    this.addMessage({ message: data });
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('chat.messageInput.sendingFailed'),
                    });
                } finally {
                    this.setSending(false);
                }
            },

            setWrapperWidth() {
                if (this.$refs.wrapper) {
                    this.wrapperWidth = this.$refs.wrapper.offsetWidth;
                } else {
                    this.wrapperWidth = 0;
                }
            },

            getAttachmentStyle(attachment) {
                const dimensions = attachment.dimensions;
                const maxWidth = this.$mq === 'xs' ? 256 : 384;
                const width = dimensions.width < maxWidth ? dimensions.width : maxWidth;
                const ratio = dimensions.width / width;

                return {
                    height: `${Math.round(dimensions.height / ratio)}px`,
                    maxWidth: `${width}px`,
                };
            },
        },

        mounted() {
            this.$nextTick(() => {
                this.setWrapperWidth();

                // Convert urls to anchor tags
                Array.from(this.$el.getElementsByTagName('p')).forEach(element => {
                    linkifyElement(element, {
                        className: 'underline',
                    });
                });
            });
        },
    };
</script>
